/**
 * Hash Generation Command
 * Generate hash using MD5, SHA, or bcrypt
 */
import { Command } from 'commander';
import { UtilsService, HashAlgorithm } from '../../services/utils.service.js';

async function handleHashGeneration(
    input: string | undefined,
    options: {
        algorithm: HashAlgorithm;
        file?: string;
        rounds: number;
    }
): Promise<void> {
    const service = new UtilsService();

    try {
        let hash: string;

        if (options.file) {
            // Hash file contents
            if (options.algorithm === 'bcrypt') {
                console.error('❌ bcrypt cannot be used with files (it requires string input)');
                process.exit(1);
            }
            hash = await service.hashFile(options.file, options.algorithm);
        } else {
            // Hash text input
            if (!input) {
                console.error('❌ Please provide input text or use --file option');
                process.exit(1);
            }

            if (options.algorithm === 'bcrypt') {
                hash = await service.hashBcrypt(input, options.rounds);
            } else {
                hash = await service.hash(input, options.algorithm);
            }
        }

        console.log('🔒 Hash Result:');
        console.log('────────────────────────────────────────');
        console.log(`   Algorithm: ${options.algorithm.toUpperCase()}`);
        if (options.file) {
            console.log(`   File: ${options.file}`);
        }
        if (options.algorithm === 'bcrypt') {
            console.log(`   Rounds: ${options.rounds}`);
        }
        console.log();
        console.log(`   ${hash}`);
        console.log();
    } catch (error) {
        console.error('❌ Error:', error instanceof Error ? error.message : String(error));
        process.exit(1);
    }
}

export function createHashCommand(): Command {
    const cmd = new Command('hash')
        .description('Generate hash (MD5, SHA, bcrypt)')
        .argument('[input]', 'Text to hash')
        .option(
            '-a, --algorithm <algorithm>',
            'Hash algorithm (md5, sha1, sha256, sha512, bcrypt)',
            'sha256'
        )
        .option('-f, --file <path>', 'Hash file contents instead of text')
        .option('-r, --rounds <number>', 'Bcrypt rounds (only for bcrypt)', '10')
        .addHelpText('after', `
Examples:
  $ jai1 utils hash "hello world"
  $ jai1 utils hash "hello" --algorithm md5
  $ jai1 utils hash "password" --algorithm bcrypt
  $ jai1 utils hash "password" --algorithm bcrypt --rounds 12
  $ jai1 utils hash --file ./myfile.txt
  $ jai1 utils hash --file ./image.png --algorithm sha512
`)
        .action(async (input, options) => {
            await handleHashGeneration(input, {
                ...options,
                rounds: parseInt(options.rounds, 10),
            });
        });

    return cmd;
}
